import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Plus, CheckCircle, XCircle, AlertTriangle, FileText, Printer } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { apiClient } from '../../utils/apiHelper';
import axios from 'axios';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TransportClearance = () => {
  const [clearances, setClearances] = useState([]);
  const [students, setStudents] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [feeDetails, setFeeDetails] = useState({ total_paid: 0, pending_dues: 0, fine_amount: 0, months_paid: [] });
  const [collegeInfo, setCollegeInfo] = useState(null);
  
  const [formData, setFormData] = useState({
    clearance_number: `TC${Date.now()}`,
    student_id: '',
    student_name: '',
    route_id: '',
    route_name: '',
    admission_date: '',
    discontinue_date: new Date().toISOString().split('T')[0],
    total_fee_paid: 0,
    pending_dues: 0,
    fine_amount: 0,
    fine_reason: '',
    remarks: '',
    issued_by: 'Admin',
    clearance_status: 'Pending'
  });

  useEffect(() => {
    fetchClearances();
    fetchTransportStudents();
    fetchRoutes();
    loadCollegeInfo();
  }, []);

  const loadCollegeInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/tenants/my-college/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setCollegeInfo({
          ...response.data,
          college_name: response.data.name || response.data.college_name,
          logo_url: response.data.logo || response.data.logo_url
        });
      }
    } catch (error) {
      console.error('Error fetching college info:', error);
    }
  };

  const fetchClearances = async () => {
    try {
      const response = await apiClient.get('/api/transport/clearances');
      setClearances(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching clearances:', error);
    }
  };

  const fetchTransportStudents = async () => {
    try {
      const response = await apiClient.get('/api/transport/students');
      // Only show active students for clearance
      const activeStudents = (response.data || []).filter(s => s.status === 'Active');
      setStudents(activeStudents);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const fetchRoutes = async () => {
    try {
      const response = await apiClient.get('/api/transport/routes');
      setRoutes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const calculateDues = async (studentId, student) => {
    try {
      // Fetch all payments for this student
      const paymentsRes = await apiClient.get(`/api/transport/fee-payments`);
      const allPayments = paymentsRes.data || [];
      const studentPayments = allPayments.filter(p => p.student_id === studentId);
      
      // Calculate total paid
      const totalPaid = studentPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
      
      // Get months paid
      const monthsPaid = studentPayments.map(p => `${p.month}/${p.year}`);
      
      // Calculate expected payments (from admission to discontinue date)
      const admissionDate = new Date(student.admission_date);
      const discontinueDate = new Date(formData.discontinue_date);
      
      // Count months between admission and discontinue
      let monthsDiff = (discontinueDate.getFullYear() - admissionDate.getFullYear()) * 12;
      monthsDiff += discontinueDate.getMonth() - admissionDate.getMonth() + 1;
      
      const monthlyFee = student.monthly_fee || 0;
      const expectedTotal = monthsDiff * monthlyFee;
      const pendingDues = Math.max(0, expectedTotal - totalPaid);
      
      setFeeDetails({
        total_paid: totalPaid,
        pending_dues: pendingDues,
        fine_amount: 0,
        months_paid: monthsPaid,
        expected_total: expectedTotal,
        months_count: monthsDiff
      });
      
      setFormData(prev => ({
        ...prev,
        total_fee_paid: totalPaid,
        pending_dues: pendingDues
      }));
      
    } catch (error) {
      console.error('Error calculating dues:', error);
    }
  };

  const handleStudentChange = async (studentId) => {
    const student = students.find(s => s.student_id === studentId);
    if (student) {
      setSelectedStudent(student);
      setFormData(prev => ({
        ...prev,
        student_id: studentId,
        student_name: student.student_name,
        route_id: student.route_id,
        route_name: student.route_name,
        admission_date: student.admission_date
      }));
      
      await calculateDues(studentId, student);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if there are pending dues
    if (feeDetails.pending_dues > 0 && formData.fine_amount === 0) {
      const confirmProceed = window.confirm(
        `This student has pending dues of ₹${feeDetails.pending_dues}. \n\nYou cannot issue clearance without clearing all dues. \n\nWould you like to add this as fine/adjustment?`
      );
      if (confirmProceed) {
        setFormData(prev => ({
          ...prev,
          fine_amount: feeDetails.pending_dues,
          fine_reason: 'Pending transport fees'
        }));
        return;
      } else {
        alert('Please clear all pending dues before issuing clearance.');
        return;
      }
    }
    
    setLoading(true);
    try {
      const response = await apiClient.post('/api/transport/clearances', formData);
      if (response.data) {
        // Update student status to Discontinued
        await apiClient.put(`/api/transport/students/${formData.student_id}/discontinue?end_date=${formData.discontinue_date}`);
        
        alert('Clearance created successfully!');
        fetchClearances();
        fetchTransportStudents();
        handleCloseModal();
      }
    } catch (error) {
      alert('Failed to create clearance');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (clearance) => {
    if (clearance.pending_dues > 0) {
      alert('Cannot approve clearance with pending dues!');
      return;
    }
    
    const issuedBy = prompt('Approved by (name):');
    if (!issuedBy) return;
    
    try {
      await apiClient.put(`/api/transport/clearances/${clearance.id}/approve`, {
        issue_date: new Date().toISOString().split('T')[0],
        issued_by: issuedBy
      });
      fetchClearances();
      alert('Clearance approved!');
    } catch (error) {
      alert('Failed to approve clearance');
    }
  };

  const handleReject = async (clearanceId) => {
    const reason = prompt('Rejection reason:');
    if (!reason) return;
    
    try {
      await apiClient.put(`/api/transport/clearances/${clearanceId}/reject`, {
        rejection_reason: reason
      });
      fetchClearances();
      alert('Clearance rejected.');
    } catch (error) {
      alert('Failed to reject clearance');
    }
  };

  const handlePrintCertificate = (clearance) => {
    // Use collegeInfo from state instead of localStorage
    const tenantInfo = collegeInfo || {};
    
    const printContent = `
      <html>
        <head>
          <title>Transport Clearance Certificate</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
            .tenant-header { text-align: center; margin-bottom: 20px; border-bottom: 1px solid #ddd; padding-bottom: 15px; }
            .tenant-header img { max-height: 60px; margin-bottom: 10px; }
            .tenant-header h2 { margin: 5px 0; color: #333; }
            .tenant-header p { margin: 2px 0; font-size: 12px; color: #666; }
            .tenant-header .website { color: #0066cc; }
            .header { text-align: center; padding: 20px 0; }
            .header h1 { margin: 0; font-size: 24px; border: 2px solid #333; display: inline-block; padding: 10px 30px; }
            .content { margin-top: 30px; }
            .row { display: flex; margin-bottom: 10px; padding: 8px 0; border-bottom: 1px solid #eee; }
            .label { font-weight: bold; width: 200px; color: #555; }
            .footer { margin-top: 50px; text-align: right; }
            .stamp { border: 2px solid green; padding: 10px 20px; display: inline-block; color: green; font-weight: bold; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          <div class="tenant-header">
            ${tenantInfo.logo_url ? `<img src="${tenantInfo.logo_url}" alt="Logo" />` : ''}
            <h2>${tenantInfo.college_name || tenantInfo.name || 'Institution Name'}</h2>
            <p>${tenantInfo.address || ''}</p>
            <p>${tenantInfo.phone ? 'Phone: ' + tenantInfo.phone : ''} ${tenantInfo.email ? ' | Email: ' + tenantInfo.email : ''}</p>
            ${tenantInfo.website ? `<p class="website">${tenantInfo.website}</p>` : ''}
          </div>
          <div class="header">
            <h1>TRANSPORT CLEARANCE CERTIFICATE</h1>
            <p style="margin-top: 10px;">Certificate No: ${clearance.clearance_number}</p>
          </div>
          <div class="content">
            <div class="row"><span class="label">Student Name:</span> ${clearance.student_name}</div>
            <div class="row"><span class="label">Route:</span> ${clearance.route_name}</div>
            <div class="row"><span class="label">Admission Date:</span> ${clearance.admission_date}</div>
            <div class="row"><span class="label">Discontinue Date:</span> ${clearance.discontinue_date}</div>
            <div class="row"><span class="label">Total Fee Paid:</span> ₹${clearance.total_fee_paid}</div>
            <div class="row"><span class="label">Pending Dues:</span> ₹${clearance.pending_dues}</div>
            <div class="row"><span class="label">Status:</span> ${clearance.clearance_status}</div>
            <p style="margin-top: 30px;">This is to certify that the above-mentioned student has cleared all transport dues and is hereby granted clearance from the transport facility.</p>
          </div>
          <div class="footer">
            <p>Issued By: ${clearance.issued_by}</p>
            <p>Date: ${clearance.issue_date || formatDate()}</p>
            <div class="stamp">CLEARED</div>
          </div>
        </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedStudent(null);
    setFeeDetails({ total_paid: 0, pending_dues: 0, fine_amount: 0, months_paid: [] });
    setFormData({
      clearance_number: `TC${Date.now()}`,
      student_id: '',
      student_name: '',
      route_id: '',
      route_name: '',
      admission_date: '',
      discontinue_date: new Date().toISOString().split('T')[0],
      total_fee_paid: 0,
      pending_dues: 0,
      fine_amount: 0,
      fine_reason: '',
      remarks: '',
      issued_by: 'Admin',
      clearance_status: 'Pending'
    });
  };

  // Summary stats
  const pendingCount = clearances.filter(c => c.clearance_status === 'Pending').length;
  const clearedCount = clearances.filter(c => c.clearance_status === 'Cleared').length;
  const rejectedCount = clearances.filter(c => c.clearance_status === 'Rejected').length;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="w-6 h-6 text-blue-600" />
            Transport Clearance
          </h1>
          <p className="text-gray-500 text-sm">Manage student discontinuation and clearance certificates</p>
        </div>
        <Button onClick={() => setIsModalOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Create Clearance
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <p className="text-sm text-blue-600">Total Requests</p>
            <p className="text-2xl font-bold text-blue-700">{clearances.length}</p>
          </CardContent>
        </Card>
        <Card className={`${pendingCount > 0 ? 'bg-yellow-50 border-yellow-200' : 'bg-gray-50 border-gray-200'}`}>
          <CardContent className="p-4">
            <p className={`text-sm ${pendingCount > 0 ? 'text-yellow-600' : 'text-gray-500'}`}>Pending</p>
            <p className={`text-2xl font-bold ${pendingCount > 0 ? 'text-yellow-700' : 'text-gray-600'}`}>{pendingCount}</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <p className="text-sm text-green-600">Cleared</p>
            <p className="text-2xl font-bold text-green-700">{clearedCount}</p>
          </CardContent>
        </Card>
        <Card className={`${rejectedCount > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
          <CardContent className="p-4">
            <p className={`text-sm ${rejectedCount > 0 ? 'text-red-600' : 'text-gray-500'}`}>Rejected</p>
            <p className={`text-2xl font-bold ${rejectedCount > 0 ? 'text-red-700' : 'text-gray-600'}`}>{rejectedCount}</p>
          </CardContent>
        </Card>
      </div>

      {/* Clearances Table */}
      <Card>
        <CardContent className="pt-4">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left p-3">Clearance #</th>
                <th className="text-left p-3">Student</th>
                <th className="text-left p-3">Route</th>
                <th className="text-left p-3">Discontinue Date</th>
                <th className="text-left p-3">Total Paid</th>
                <th className="text-left p-3">Pending Dues</th>
                <th className="text-left p-3">Fine</th>
                <th className="text-center p-3">Status</th>
                <th className="text-center p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {clearances.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center p-8 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>No clearance requests found.</p>
                  </td>
                </tr>
              ) : (
                clearances.map((clearance) => (
                  <tr key={clearance.id} className={`border-b hover:bg-gray-50 ${clearance.pending_dues > 0 ? 'bg-red-50' : ''}`}>
                    <td className="p-3 font-medium">{clearance.clearance_number}</td>
                    <td className="p-3">{clearance.student_name}</td>
                    <td className="p-3">{clearance.route_name}</td>
                    <td className="p-3">{clearance.discontinue_date}</td>
                    <td className="p-3 text-green-600">₹{clearance.total_fee_paid}</td>
                    <td className="p-3">
                      {clearance.pending_dues > 0 ? (
                        <span className="text-red-600 flex items-center gap-1">
                          <AlertTriangle className="w-4 h-4" />
                          ₹{clearance.pending_dues}
                        </span>
                      ) : (
                        <span className="text-green-600">₹0</span>
                      )}
                    </td>
                    <td className="p-3 text-orange-600">₹{clearance.fine_amount || 0}</td>
                    <td className="p-3 text-center">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        clearance.clearance_status === 'Cleared' ? 'bg-green-100 text-green-800' : 
                        clearance.clearance_status === 'Rejected' ? 'bg-red-100 text-red-800' : 
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {clearance.clearance_status}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex gap-2 justify-center">
                        {clearance.clearance_status === 'Pending' && (
                          <>
                            <Button size="sm" variant="outline" className="text-green-600" onClick={() => handleApprove(clearance)}>
                              <CheckCircle className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleReject(clearance.id)}>
                              <XCircle className="w-3 h-3" />
                            </Button>
                          </>
                        )}
                        {clearance.clearance_status === 'Cleared' && (
                          <Button size="sm" variant="outline" onClick={() => handlePrintCertificate(clearance)} title="Print on Letterhead">
                            <Printer className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Create Clearance Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Clearance Request</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Student Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Clearance Number *</Label>
                <Input required value={formData.clearance_number} readOnly className="bg-gray-100" />
              </div>
              <div>
                <Label>Select Student *</Label>
                <Select value={formData.student_id} onValueChange={handleStudentChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="-- Select Student --" />
                  </SelectTrigger>
                  <SelectContent>
                    {students.length === 0 ? (
                      <SelectItem value="__no_active__" disabled>No active students</SelectItem>
                    ) : (
                      students.map((s) => (
                        <SelectItem key={s.id} value={s.student_id}>
                          {s.student_name} - {s.route_name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Student Details (shown after selection) */}
            {selectedStudent && (
              <>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium mb-2">Student Details</h4>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div><span className="text-gray-500">Route:</span> {selectedStudent.route_name}</div>
                    <div><span className="text-gray-500">Monthly Fee:</span> ₹{selectedStudent.monthly_fee}</div>
                    <div><span className="text-gray-500">Admission:</span> {selectedStudent.admission_date}</div>
                  </div>
                </div>

                {/* Fee Summary */}
                <div className={`p-4 rounded-lg ${feeDetails.pending_dues > 0 ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    {feeDetails.pending_dues > 0 && <AlertTriangle className="w-4 h-4 text-red-500" />}
                    Fee Summary
                  </h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Total Paid</p>
                      <p className="text-lg font-bold text-green-600">₹{feeDetails.total_paid}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Expected ({feeDetails.months_count} months)</p>
                      <p className="text-lg font-bold text-blue-600">₹{feeDetails.expected_total}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Pending Dues</p>
                      <p className={`text-lg font-bold ${feeDetails.pending_dues > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        ₹{feeDetails.pending_dues}
                      </p>
                    </div>
                  </div>
                  {feeDetails.pending_dues > 0 && (
                    <p className="text-red-600 text-sm mt-2">
                      ⚠️ Student has pending dues. Clearance cannot be issued without clearing all dues.
                    </p>
                  )}
                </div>
              </>
            )}

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Admission Date</Label>
                <Input value={formData.admission_date} readOnly className="bg-gray-100" />
              </div>
              <div>
                <Label>Discontinue Date *</Label>
                <Input 
                  type="date" 
                  required 
                  value={formData.discontinue_date} 
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, discontinue_date: e.target.value }));
                    if (selectedStudent) {
                      calculateDues(formData.student_id, selectedStudent);
                    }
                  }} 
                />
              </div>
            </div>

            {/* Fine (if applicable) */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fine Amount (if any)</Label>
                <Input 
                  type="number" 
                  value={formData.fine_amount} 
                  onChange={(e) => setFormData(prev => ({ ...prev, fine_amount: parseFloat(e.target.value) || 0 }))} 
                />
              </div>
              <div>
                <Label>Fine Reason</Label>
                <Input 
                  value={formData.fine_reason} 
                  onChange={(e) => setFormData(prev => ({ ...prev, fine_reason: e.target.value }))} 
                  placeholder="e.g., Late payment, Damage, etc."
                />
              </div>
            </div>

            {/* Remarks */}
            <div>
              <Label>Remarks</Label>
              <Textarea 
                value={formData.remarks} 
                onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))} 
                placeholder="Any additional remarks..."
                rows={2}
              />
            </div>

            <div>
              <Label>Issued By *</Label>
              <Input 
                required 
                value={formData.issued_by} 
                onChange={(e) => setFormData(prev => ({ ...prev, issued_by: e.target.value }))} 
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseModal}>Cancel</Button>
              <Button 
                type="submit" 
                disabled={loading || !formData.student_id}
              >
                {loading ? 'Creating...' : 'Create Clearance'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TransportClearance;
